/**
 * Document-review domain schemas, derived from the generated Zod.
 *
 * The app-side enrichments layered on top:
 *   - the shared lenient `attachmentSchema` in place of the generated strict
 *     Attachment sub-object (whose `file` is a `.url().regex()`; the app reads
 *     media paths and fixtures);
 *   - typed-JSON views (the `extractedData` / `approvedData` / `splitMetadata`
 *     records) over generated `unknown` blobs;
 *   - a `z.literal(...)` narrowing on each action response, tightening the
 *     generated status enum to the one status that action guarantees;
 *   - presence-tolerance on a few fields lean rows and fixtures omit.
 *
 * See ./README.md.
 */
import { z } from 'zod';

import {
  DocumentUploadsDocumentsApproveCreateResponse,
  DocumentUploadsDocumentsFlagCreateResponse,
  DocumentUploadsDocumentsReclassifyCreateResponse,
  DocumentUploadsDocumentsRejectCreateResponse,
  DocumentUploadsDocumentsResolveFlagCreateResponse,
  DocumentUploadsDocumentsRetryExtractionCreateResponse,
  DocumentUploadsListResponse,
  DocumentUploadsRetrieveResponse,
  DocumentUploadsSplitReviewApproveCreateResponse,
  DocumentUploadsSplitReviewRetrieveResponse,
} from '~/data/generated/zod/document-uploads/document-uploads.zod';
import { InboundEmailsRetrieveResponse } from '~/data/generated/zod/inbound-emails/inbound-emails.zod';
import { ReviewQueueListResponse } from '~/data/generated/zod/review-queue/review-queue.zod';
import { attachmentSchema } from '~/data/schemas/attachment';

import { type OneOrMore, type Values } from './zodHelpers';

// =============================================================================
// Document Classification
// =============================================================================

export const ProcessedDocumentClassification = {
  Invoice: 'invoice',
  Ticket: 'ticket',
  Certificate: 'certificate',
  InspectionReport: 'inspection_report',
  Contract: 'contract',
  Violation: 'violation',
} as const;

export type ProcessedDocumentClassificationKey = Values<typeof ProcessedDocumentClassification>;
const processedDocumentClassificationValues = Object.values(
  ProcessedDocumentClassification,
) as OneOrMore<ProcessedDocumentClassificationKey>;

export const processedDocumentClassificationOptions = [
  [ProcessedDocumentClassification.Invoice, 'Invoice'],
  [ProcessedDocumentClassification.Ticket, 'Ticket'],
  [ProcessedDocumentClassification.Certificate, 'Certificate'],
  [ProcessedDocumentClassification.InspectionReport, 'Inspection Report'],
  [ProcessedDocumentClassification.Contract, 'Contract'],
  [ProcessedDocumentClassification.Violation, 'Violation'],
] as const;

export const processedDocumentClassificationToLabelMap = new Map(
  processedDocumentClassificationOptions,
);

// =============================================================================
// Document Extraction Status
// =============================================================================

export const ProcessedDocumentExtractionStatus = {
  NotAttempted: 'not_attempted',
  InProgress: 'in_progress',
  Completed: 'completed',
  Failed: 'failed',
  NotSupported: 'not_supported',
} as const;

const processedDocumentExtractionStatusOptions = [
  [ProcessedDocumentExtractionStatus.NotAttempted, 'Not Attempted'],
  [ProcessedDocumentExtractionStatus.InProgress, 'In Progress'],
  [ProcessedDocumentExtractionStatus.Completed, 'Completed'],
  [ProcessedDocumentExtractionStatus.Failed, 'Failed'],
  [ProcessedDocumentExtractionStatus.NotSupported, 'Not Supported'],
] as const;

export const processedDocumentExtractionStatusToLabelMap = new Map(
  processedDocumentExtractionStatusOptions,
);

// =============================================================================
// Document Review Status
// =============================================================================

export const ProcessedDocumentReviewStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
  Flagged: 'flagged',
  ManuallyResolved: 'manually_resolved',
} as const;

const processedDocumentReviewStatusOptions = [
  [ProcessedDocumentReviewStatus.Pending, 'Pending'],
  [ProcessedDocumentReviewStatus.Approved, 'Approved'],
  [ProcessedDocumentReviewStatus.Rejected, 'Rejected'],
  [ProcessedDocumentReviewStatus.Flagged, 'Flagged'],
  [ProcessedDocumentReviewStatus.ManuallyResolved, 'Manually Resolved'],
] as const;

export const processedDocumentReviewStatusToLabelMap = new Map(
  processedDocumentReviewStatusOptions,
);

export const processedDocumentReviewStatusToColorMap = new Map([
  [ProcessedDocumentReviewStatus.Pending, 'info'],
  [ProcessedDocumentReviewStatus.Approved, 'success'],
  [ProcessedDocumentReviewStatus.Rejected, 'error'],
  [ProcessedDocumentReviewStatus.Flagged, 'warning'],
  [ProcessedDocumentReviewStatus.ManuallyResolved, 'info'],
] as const);

// =============================================================================
// Document Upload Status
// =============================================================================

export const DocumentUploadStatus = {
  Pending: 'pending',
  Splitting: 'splitting',
  AwaitingSplitReview: 'awaiting_split_review',
  AwaitingRecordReview: 'awaiting_record_review',
  Classifying: 'classifying',
  Extracting: 'extracting',
  Ready: 'ready',
  Completed: 'completed',
  Failed: 'failed',
} as const;

export type DocumentUploadStatusKey = Values<typeof DocumentUploadStatus>;

const documentUploadStatusOptions = [
  [DocumentUploadStatus.Pending, 'Pending'],
  [DocumentUploadStatus.Splitting, 'Splitting'],
  [DocumentUploadStatus.AwaitingSplitReview, 'Review needed'],
  [DocumentUploadStatus.AwaitingRecordReview, 'Record review needed'],
  [DocumentUploadStatus.Classifying, 'Classifying'],
  [DocumentUploadStatus.Extracting, 'Extracting'],
  [DocumentUploadStatus.Ready, 'Ready'],
  [DocumentUploadStatus.Completed, 'Completed'],
  [DocumentUploadStatus.Failed, 'Failed'],
] as const;

export const documentUploadStatusToLabelMap = new Map(documentUploadStatusOptions);

export const documentUploadStatusToColorMap = new Map([
  [DocumentUploadStatus.Pending, 'info'],
  [DocumentUploadStatus.Splitting, 'info'],
  [DocumentUploadStatus.AwaitingSplitReview, 'warning'],
  [DocumentUploadStatus.AwaitingRecordReview, 'warning'],
  [DocumentUploadStatus.Classifying, 'info'],
  [DocumentUploadStatus.Extracting, 'info'],
  [DocumentUploadStatus.Ready, 'warning'],
  [DocumentUploadStatus.Completed, 'success'],
  [DocumentUploadStatus.Failed, 'error'],
] as const);

// =============================================================================
// Document Type Hint
// =============================================================================

export const DocumentTypeHint = {
  Auto: 'auto',
  Contract: 'contract',
  Single: 'single',
} as const;

// =============================================================================
// Suggested Match Schema
// =============================================================================

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const suggestedMatchSchema = z.object({
  id: z.string(),
  display: z.string(),
  confidence: z.number(),
});

export type SuggestedMatch = z.infer<typeof suggestedMatchSchema>;

// `suggestedMatchesSchema` / `SuggestedMatches` are derived from the generated
// Zod below (once `processedDocumentReadBase` is in scope) — the API now emits
// a typed `SuggestedMatches` component, so the boundary schema is generated,
// not hand-rolled.

// =============================================================================
// Processed Document Schemas
// =============================================================================

// The wire is DETAIL-shaped even on the list endpoints — every row inlines
// `extractedData`/`approvedData`/`suggestedMatches`, as there is no leaner list
// serializer. So the app's list and detail schemas rebase on the same element.
const processedDocumentReadBase = ReviewQueueListResponse.shape.results.element;

// The API self-describes `suggested_matches` as a typed `SuggestedMatches`
// component (heterogeneous map: flat `SuggestionMatch[]` per FK field, plus the
// nested `propertyContracts` shape), so the boundary schema is the generated
// one — not a hand-rolled `z.record`, which mis-modelled `propertyContracts`
// and was the loudest live drift path in boundary telemetry (AMP-656).
const suggestedMatchesSchema = processedDocumentReadBase.shape.suggestedMatches;

export type SuggestedMatches = z.infer<typeof suggestedMatchesSchema>;

/**
 * Base schema for ProcessedDocument used in list views
 */
export const processedDocumentListSchema = processedDocumentReadBase
  .omit({ extractedData: true, approvedData: true, suggestedMatches: true })
  .extend({
    // Generated inlines the strict Attachment sub-object (`file` is
    // `.url().regex()`); the app reads a lenient attachment.
    attachment: attachmentSchema,
    // Presence-tolerance for lean rows / fixtures: non-null on the wire, but the
    // key can be absent. Tolerant of absence, NOT nullable.
    feedback: processedDocumentReadBase.shape.feedback.optional(),
    reviewedByName: processedDocumentReadBase.shape.reviewedByName.optional(),
    autoApproved: z.boolean().default(false),
    autoApprovalConfidence: processedDocumentReadBase.shape.autoApprovalConfidence.optional(),
  });

/**
 * Detailed schema for ProcessedDocument including extracted_data and suggested_matches
 */
export const processedDocumentDetailSchema = processedDocumentListSchema.extend({
  // Typed-JSON views over the generated `unknown` blobs.
  extractedData: z.record(z.string(), z.unknown()).nullable(),
  approvedData: z.record(z.string(), z.unknown()).nullable(),
  // Restores the generated `SuggestedMatches` shape the list base omits.
  suggestedMatches: suggestedMatchesSchema,
});

export type ProcessedDocumentDetail = z.infer<typeof processedDocumentDetailSchema>;

// =============================================================================
// Document Upload Source
// =============================================================================

export const DocumentUploadSource = {
  Manual: 'manual',
  Email: 'email',
} as const;

type DocumentUploadSourceKey = Values<typeof DocumentUploadSource>;
export const documentUploadSourceValues = Object.values(
  DocumentUploadSource,
) as OneOrMore<DocumentUploadSourceKey>;

/**
 * Schema for email context metadata on email-sourced uploads
 */
export const emailContextSchema = z.object({
  sender: z.string(),
  subject: z.string(),
  textBody: z.string().nullable().optional(),
  receivedAt: z.string(),
});

// =============================================================================
// Document Upload Schema
// =============================================================================

/**
 * Summary schema for ProcessedDocument used in DocumentUploadDetail
 */
const processedDocumentSummarySchema =
  DocumentUploadsRetrieveResponse.shape.processedDocuments.element.extend({
    // Presence-tolerance for lean rows / fixtures (see processedDocumentListSchema).
    autoApproved: z.boolean().default(false),
    autoApprovalConfidence:
      DocumentUploadsRetrieveResponse.shape.processedDocuments.element.shape.autoApprovalConfidence.optional(),
  });

export type ProcessedDocumentSummary = z.infer<typeof processedDocumentSummarySchema>;

/**
 * Schema for document upload (list/create responses)
 */
export const documentUploadSchema = DocumentUploadsListResponse.shape.results.element.extend({
  // Generated types originalAttachment as the strict Attachment sub-object;
  // adopt the lenient shared attachmentSchema (media paths / fixtures).
  originalAttachment: attachmentSchema,
  // Wire emits null for a still-processing or successful upload.
  errorMessage: DocumentUploadsListResponse.shape.results.element.shape.errorMessage.nullable(),
});

export type DocumentUpload = z.infer<typeof documentUploadSchema>;

// =============================================================================
// Inbound Email Schema
// =============================================================================

/**
 * Schema for document upload nested within InboundEmail responses
 */
const inboundEmailUploadSchema = InboundEmailsRetrieveResponse.shape.documentUploads.element.extend(
  {
    originalAttachment: attachmentSchema,
    // See documentUploadSchema: errorMessage is null on a still-processing /
    // successful upload despite the generated read typing it non-null.
    errorMessage:
      InboundEmailsRetrieveResponse.shape.documentUploads.element.shape.errorMessage.nullable(),
    processedDocuments: z.array(processedDocumentSummarySchema).default([]),
  },
);

export type InboundEmailUpload = z.infer<typeof inboundEmailUploadSchema>;

/**
 * Schema for inbound email with nested document uploads
 */
export const inboundEmailSchema = InboundEmailsRetrieveResponse.extend({
  documentUploads: z.array(inboundEmailUploadSchema),
});

export type InboundEmail = z.infer<typeof inboundEmailSchema>;

/**
 * Schema for document upload detail (includes processed documents)
 */
export const documentUploadDetailSchema = DocumentUploadsRetrieveResponse.extend({
  originalAttachment: attachmentSchema,
  // See documentUploadSchema: errorMessage is null on a still-processing /
  // successful upload despite the generated read typing it non-null.
  errorMessage: DocumentUploadsRetrieveResponse.shape.errorMessage.nullable(),
  // Typed-JSON view over the generated `unknown` split-metadata blob.
  splitMetadata: z.record(z.unknown()).nullable(),
  processedDocuments: z.array(processedDocumentSummarySchema),
});

export type DocumentUploadDetail = z.infer<typeof documentUploadDetailSchema>;

// =============================================================================
// Split Review Schemas
// =============================================================================

const splitBoundarySchema =
  DocumentUploadsSplitReviewRetrieveResponse.shape.boundaries.element.extend({
    // Generated omits a default; the app treats an absent vote list as empty.
    votes: z.array(z.number()).default([]),
  });
export type SplitBoundary = z.infer<typeof splitBoundarySchema>;

export const splitReviewSchema = DocumentUploadsSplitReviewRetrieveResponse.extend({
  // Generated types this `.url()`; keep the lenient string (media paths).
  originalAttachmentUrl: z.string().nullable(),
  boundaries: z.array(splitBoundarySchema),
});
export type SplitReview = z.infer<typeof splitReviewSchema>;

export const splitReviewApproveResponseSchema = DocumentUploadsSplitReviewApproveCreateResponse;
export type SplitReviewApproveResponse = z.infer<typeof splitReviewApproveResponseSchema>;

// =============================================================================
// API Request Schemas - Review Actions
// =============================================================================

/**
 * Request schema for approving a document and creating an entity
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const approveDocumentRequestSchema = z.object({
  entityData: z.record(z.string(), z.unknown()).optional(),
  feedback: z.string().optional(),
});

export type ApproveDocumentRequest = z.infer<typeof approveDocumentRequestSchema>;

/**
 * Request schema for rejecting a document
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const rejectDocumentRequestSchema = z.object({
  feedback: z.string().optional(),
});

export type RejectDocumentRequest = z.infer<typeof rejectDocumentRequestSchema>;

/**
 * Request schema for reclassifying a document
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const reclassifyDocumentRequestSchema = z.object({
  classification: z.enum(processedDocumentClassificationValues),
});

export type ReclassifyDocumentRequest = z.infer<typeof reclassifyDocumentRequestSchema>;

/**
 * Request schema for flagging a document for legacy processing
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const flagDocumentRequestSchema = z.object({
  reason: z.string(),
});

export type FlagDocumentRequest = z.infer<typeof flagDocumentRequestSchema>;

// =============================================================================
// API Response Schemas
// =============================================================================

/**
 * Response schema for approve action - returns the created entity ID.
 * The action guarantees an approved review status, so narrow the generated
 * plain review-status enum to that literal.
 */
export const approveDocumentResponseSchema = DocumentUploadsDocumentsApproveCreateResponse.extend({
  reviewStatus: z.literal(ProcessedDocumentReviewStatus.Approved),
});

export type ApproveDocumentResponse = z.infer<typeof approveDocumentResponseSchema>;

/**
 * Response schema for reject action
 */
export const rejectDocumentResponseSchema = DocumentUploadsDocumentsRejectCreateResponse.extend({
  reviewStatus: z.literal(ProcessedDocumentReviewStatus.Rejected),
});

export type RejectDocumentResponse = z.infer<typeof rejectDocumentResponseSchema>;

/**
 * Response schema for reclassify action
 */
export const reclassifyDocumentResponseSchema = DocumentUploadsDocumentsReclassifyCreateResponse;

export type ReclassifyDocumentResponse = z.infer<typeof reclassifyDocumentResponseSchema>;

/**
 * Response schema for retry-extraction action
 */
export const retryExtractionResponseSchema = DocumentUploadsDocumentsRetryExtractionCreateResponse;

export type RetryExtractionResponse = z.infer<typeof retryExtractionResponseSchema>;

/**
 * Response schema for flag action
 */
export const flagDocumentResponseSchema = DocumentUploadsDocumentsFlagCreateResponse.extend({
  reviewStatus: z.literal(ProcessedDocumentReviewStatus.Flagged),
});

export type FlagDocumentResponse = z.infer<typeof flagDocumentResponseSchema>;

/**
 * Response schema for resolve-flag action
 */
export const resolveFlagDocumentResponseSchema =
  DocumentUploadsDocumentsResolveFlagCreateResponse.extend({
    reviewStatus: z.literal(ProcessedDocumentReviewStatus.ManuallyResolved),
  });

export type ResolveFlagDocumentResponse = z.infer<typeof resolveFlagDocumentResponseSchema>;

// =============================================================================
// Contract Extraction Types (matches backend ContractExtraction schema)
//
// The extraction schemas below type the untyped AI-extraction JSON blobs the
// pipeline emits. They are NEVER parsed through `parseApiResponse`, so they have
// no generated counterpart and stay hand-written.
// =============================================================================

const extractedRateSchema = z.object({
  workType: z.string(),
  workerType: z.string(),
  rateType: z.string(),
  hourlyRate: z.number(),
  isDefault: z.boolean().optional().default(false),
  coverageType: z.string().optional().default('billable'),
  description: z.string().nullable().optional(),
});

const extractedRateSetSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  rates: z.array(extractedRateSchema).default([]),
});

const extractedCarSchema = z.object({
  // Identifiers
  name: z.string().nullable().optional(),
  machineNumber: z.string().nullable().optional(),
  otherIdentifiers: z.array(z.string()).default([]),
  // KPI targets
  maintenanceQuantity: z.number().nullable().optional(),
  maintenanceUnit: z.string().nullable().optional(),
  maintenanceTimePeriod: z.number().nullable().optional(),
  regularCallbackRate: z.number().nullable().optional(),
  emergencyCallbackRate: z.number().nullable().optional(),
  regularCallbackResponseTime: z.number().nullable().optional(),
  emergencyCallbackResponseTime: z.number().nullable().optional(),
});

// =============================================================================
// Onboarding Resolution Envelope
// =============================================================================

/**
 * A single candidate match from the backend's FK-resolution search.
 * Reuses suggestedMatchSchema shape but is embedded inside the resolution envelope.
 */
const resolutionCandidateSchema = z.object({
  id: z.string(),
  display: z.string(),
  confidence: z.number(),
});

/**
 * The resolution envelope attached to each property-contract and car dict
 * when allow_record_creation is active.  Written by the backend during FK
 * resolution; confirmed / edited by the reviewer in the wizard; carried back
 * to the approve endpoint verbatim.
 */
const propertyResolutionSchema = z.object({
  action: z.enum(['match', 'create']),
  create: z
    .object({
      name: z.string(),
      addressRaw: z.string(),
    })
    .optional(),
  candidateMatches: z.array(resolutionCandidateSchema).default([]),
  dedupKey: z.string(),
});
export type PropertyResolution = z.infer<typeof propertyResolutionSchema>;

const carResolutionSchema = z.object({
  action: z.enum(['match', 'create']),
  create: z
    .object({
      name: z.string(),
      ahjId: z.string().nullable().optional(),
    })
    .optional(),
  candidateMatches: z.array(resolutionCandidateSchema).default([]),
  dedupKey: z.string(),
});
export type CarResolution = z.infer<typeof carResolutionSchema>;

const extractedPropertyContractCarSchema = z.object({
  // Resolved data
  carId: z.string().nullable().optional(),
  carName: z.string().nullable().optional(),
  // Extracted data
  extracted: extractedCarSchema.optional(),
  // Onboarding resolution envelope (present when allow_record_creation is active)
  resolution: carResolutionSchema.optional(),
});

const extractedPropertyContractSchema = z.object({
  // Resolved data
  propertyId: z.string().nullable().optional(),
  propertyName: z.string().nullable().optional(),
  // Extracted data
  propertyText: z.string().nullable().optional(),
  monthlyPrice: z.number().nullable().optional(),
  startDate: z.string().nullable().optional(),
  expirationDate: z.string().nullable().optional(),
  rateSetName: z.string().nullable().optional(),
  cars: z.array(extractedPropertyContractCarSchema).default([]),
  // Onboarding resolution envelope (present when allow_record_creation is active)
  resolution: propertyResolutionSchema.optional(),
});

export const reviewRuleCategorySchema = z.enum([
  'review-rule',
  'included-service',
  'excluded-service',
  'miscellaneous-charge',
  'other-term',
]);

export type ReviewRuleCategory = z.infer<typeof reviewRuleCategorySchema>;

const extractedReviewRuleSchema = z.object({
  reviewRule: z.string(),
  category: reviewRuleCategorySchema.default('review-rule'),
});

export const contractExtractionSchema = z.object({
  // Basic info
  name: z.string(),
  contractPaper: z.string(),
  type: z.string(),
  term: z.string().nullable().optional(),

  // Dates
  startDate: z.string().nullable().optional(),
  expirationDate: z.string().nullable().optional(),

  // Service options
  businessHours: z.string().nullable().optional(),
  phoneMonitoring: z.boolean().nullable().optional(),
  fireService: z.boolean().nullable().optional(),
  pressureTest: z.boolean().nullable().optional(),

  // Financial terms
  partsAndMaterialsMarkup: z.number().nullable().optional(),
  hourlyFeeAdjustmentRate: z.number().nullable().optional(),
  cancellation: z.string().nullable().optional(),

  // KPI targets (contract-level)
  maintenanceCompletionTarget: z.number().nullable().optional(),
  maintenanceHoursTarget: z.number().nullable().optional(),
  uptimeTarget: z.number().nullable().optional(),
  onTimeResponseTimeTargetPct: z.number().nullable().optional(),
  emergencyCallRateTarget: z.number().nullable().optional(),
  callbackRateTarget: z.number().nullable().optional(),

  // Notes
  notes: z.string().nullable().optional(),

  // Service provider (resolved + extracted)
  serviceProvider: z.string().nullable().optional(),
  serviceProviderName: z.string().nullable().optional(),
  serviceProviderExtracted: z.string().nullable().optional(),

  // Rate sets
  rateSets: z.array(extractedRateSetSchema).default([]),

  // Property contracts (with resolved FKs)
  propertyContracts: z.array(extractedPropertyContractSchema).default([]),

  // Review rules
  reviewRules: z.array(extractedReviewRuleSchema).default([]),
});
export type ContractExtraction = z.infer<typeof contractExtractionSchema>;

// =============================================================================
// Contract FK Suggestion Types
// =============================================================================

// The contract wizard reads only the two contract-relevant keys, but the shape
// is the generated `SuggestedMatches` — pick from it so there is one source of
// truth for the `propertyContracts` shape (see AMP-656).
//
// `.strip()` is required: in Zod 3 `.pick()` inherits the parent's unknown-key
// policy, and the generated `suggestedMatchesSchema` is `.strict()`. Without it
// a `.parse()` of a real document would throw on the keys this pick drops
// (`property`, `car`, `carsServiced`, `invoice`) rather than ignoring them.
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const contractSuggestionsSchema = suggestedMatchesSchema
  .pick({
    serviceProvider: true,
    propertyContracts: true,
  })
  .strip();
export type ContractSuggestions = z.infer<typeof contractSuggestionsSchema>;

// =============================================================================
// Violation Extraction Types (matches backend ViolationExtraction schema)
// =============================================================================

const extractedViolationSchema = z.object({
  code: z.string().default(''),
  description: z.string().default(''),
  location: z.string().default(''),
  dateOpen: z.string().default(''),
  correctionDue: z.string().default(''),
  responsibility: z.string().default(''),
});

export const violationExtractionSchema = z.object({
  // List of violations
  violations: z.array(extractedViolationSchema).default([]),

  // Property (resolved + extracted)
  property: z.string().nullable().optional(),
  propertyName: z.string().nullable().optional(),
  propertyExtracted: z.string().nullable().optional(),

  // Car (resolved + extracted)
  car: z.string().nullable().optional(),
  carName: z.string().nullable().optional(),
  carExtracted: z.string().nullable().optional(),
  carMachineNumber: z.string().nullable().optional(),
  carOtherIdentifiers: z.array(z.string()).default([]),
});
export type ViolationExtraction = z.infer<typeof violationExtractionSchema>;

// =============================================================================
// Inspection Report Extraction Types (matches backend InspectionReportExtraction schema)
// =============================================================================

const inspectionViolationSchema = z.object({
  code: z.string().default(''),
  description: z.string().default(''),
  location: z.string().default(''),
});

export const inspectionReportExtractionSchema = z.object({
  // Inspection details
  inspectionDate: z.string().nullable().optional(),
  inspectionType: z.string().nullable().optional(),
  customType: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  inspectorName: z.string().nullable().optional(),
  comments: z.string().nullable().optional(),

  // Violations found during inspection
  violations: z.array(inspectionViolationSchema).default([]),

  // Property (resolved + extracted)
  property: z.string().nullable().optional(),
  propertyName: z.string().nullable().optional(),
  propertyExtracted: z.string().nullable().optional(),

  // Car (resolved + extracted)
  car: z.string().nullable().optional(),
  carName: z.string().nullable().optional(),
  carExtracted: z.string().nullable().optional(),
  carMachineNumber: z.string().nullable().optional(),
  carOtherIdentifiers: z.array(z.string()).default([]),
});
export type InspectionReportExtraction = z.infer<typeof inspectionReportExtractionSchema>;

// =============================================================================
// Ticket Extraction Types (matches backend TicketExtraction schema)
// =============================================================================

const extractedCarIdentifierSchema = z.object({
  name: z.string().default(''),
  machineNumber: z.string().default(''),
  otherIdentifiers: z.array(z.string()).default([]),
});

export const ticketExtractionSchema = z.object({
  // Ticket info
  number: z.string().nullable().optional(),
  entryType: z.string().nullable().optional(),

  // Timing
  callTime: z.string().nullable().optional(),
  startedTime: z.string().nullable().optional(),
  finishedTime: z.string().nullable().optional(),

  // Duration fields
  timeOnSiteRegularHours: z.string().nullable().optional(),
  travelTimeRegularHours: z.string().nullable().optional(),
  timeOnSiteOvertime: z.string().nullable().optional(),
  travelTimeOvertime: z.string().nullable().optional(),

  // Work details
  problemDescription: z.string().nullable().optional(),
  workPerformed: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  equipmentFailure: z.boolean().nullable().optional(),

  // Service provider (resolved + extracted)
  serviceProviderGroup: z.string().nullable().optional(),
  serviceProviderGroupName: z.string().nullable().optional(),
  serviceProviderGroupExtracted: z.string().nullable().optional(),

  // Property (resolved + extracted)
  property: z.string().nullable().optional(),
  propertyName: z.string().nullable().optional(),
  propertyExtracted: z.string().nullable().optional(),

  // Cars serviced (resolved + extracted)
  carsServiced: z.array(z.string()).default([]),
  carsServicedNames: z.array(z.string()).default([]),
  carsServicedExtracted: z.array(extractedCarIdentifierSchema).default([]),

  // Invoice (resolved + extracted)
  invoice: z.string().nullable().optional(),
  invoiceNumber: z.string().nullable().optional(),
  serviceNumber: z.string().nullable().optional(),
});
export type TicketExtraction = z.infer<typeof ticketExtractionSchema>;
